import { configuration } from 'src/configuration';

const { accountApiUrl } = configuration;

export const getStudies = async ({
  token,
  search,
  after,
}: {
  search: string;
  token: string;
  after: string;
}) => {
  const response = await fetch(
    `${accountApiUrl}/studies?search=${search}&limit=15&after=${after}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  if (data && data.studies) {
    return {
      studies: data.studies,
      lastEvaluatedKey: data.lastEvaluatedKey,
    };
  }

  return { studies: [], lastEvaluatedKey: null };
};

export async function getStudyById({ token, id }: { token: string; id: string }) {
  const response = await fetch(`${accountApiUrl}/studies/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
}

export async function getStudyOrganizations({ token, id }: { token: string; id: string }) {
  const response = await fetch(`${accountApiUrl}/studies/${id}/organizations`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
}

export async function getStudyParticipants({ token, id }: { token: string; id: string }) {
  const response = await fetch(`${accountApiUrl}/studies/${id}/participants`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
}

export async function getStudyResearchers({ token, id }: { token: string; id: string }) {
  const response = await fetch(`${accountApiUrl}/studies/${id}/researchers`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
}

export interface CreateStudyDTO {
  name: string;
  displayName: string;
  description: string;
  organizationId: string;

  startDate: string;
  completionDate: string;
  expectedParticipants: number;
  expectedParticipantDuration: string;
}

export const createStudy = async (token: string, createStudyDto: CreateStudyDTO) => {
  const response = await fetch(`${accountApiUrl}/studies`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({
      ...createStudyDto,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.study;
};

export interface UpdateStudyDTO {
  studyId: string;
  name: string;
  displayName: string;
  description: string;

  startDate?: string;
  completionDate?: string;
  expectedParticipants?: number;
  expectedParticipantDuration?: string;
}

export const updateStudy = async ({
  token,
  updatedStudy,
}: {
  token: string;
  updatedStudy: UpdateStudyDTO;
}) => {
  const response = await fetch(`${accountApiUrl}/studies/${updatedStudy.studyId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
    body: JSON.stringify({ ...updatedStudy }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.user;
};

export const deleteStudy = async ({ id, token }: { id: string; token: string }) => {
  const response = await fetch(`${accountApiUrl}/studies/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};

export const getStudyConfiguration = async ({
  studyId,
  token,
}: {
  studyId: string;
  token: string;
}): Promise<string> => {
  const response = await fetch(`${accountApiUrl}/studies/${studyId}/config`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return JSON.stringify(data, null, 2);
};

export const updateStudyConfiguration = async ({
  token,
  payload,
  studyId,
}: {
  token: string;
  payload: any;
  studyId: string;
}): Promise<void> => {
  try {
    const response = await fetch(`${accountApiUrl}/studies/${studyId}/config`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    await response.json();
    return;
  } catch (error) {
    return;
  }
};

export const regenerateStudyInvitationCode = async ({
  studyId,
  token,
}: {
  studyId: string;
  token: string;
}): Promise<string> => {
  const response = await fetch(`${accountApiUrl}/studies/${studyId}/invitation`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'PUT',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();
  return data.code;
};
